import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Routes, Route, Navigate } from "react-router-dom";
import useAuthStore from "./shared/zustand/authStore";
import AuthPage from "./widgets/login/AuthPage";
import AppLayout from "./shared/layouts/AppLayout";
import SettingsPage from "./widgets/settings/components/settingsPage";
import { useState, useEffect, Suspense } from "react";
import Lottie from "react-lottie-player";
import NotFoundAnimation from "@/assets/animations/notfoundAnimation.json";
import useToastStore from "./shared/zustand/toastStore";
import { ProgressSpinner } from "primereact/progressspinner";
import { getCWUser, getUserSession, listCWUsers, setCwUserScopes, updateCWDatabase, } from "./core/api/api";
import AppErrorBoundary from "./components/AppErrorBounday";
import ProjectAccess from "./widgets/projectCreation/components/ProjectAccess";
const UnsupportedMobileScreen = () => (_jsx("div", { className: "fixed inset-0 flex items-center justify-center bg-white text-black text-center p-4", children: _jsxs("div", { children: [_jsx(Lottie, { animationData: NotFoundAnimation, loop: true, play: true, className: "max-w-[300px] mx-auto mb-5" }), _jsx("h1", { className: "text-xl font-bold font-heading", children: "Mobile & Tablets Not Supported" }), _jsx("p", { className: "mt-2 text-gray-600", children: "Please use a desktop browser for the best experience." })] }) }));
const AppLoading = () => (_jsx("div", { className: "w-full flex items-center justify-center h-[100dvh] bg-white", children: _jsx(ProgressSpinner, { className: "size-10 text-blue-500", strokeWidth: "3" }) }));
const App = () => {
    const { showToast } = useToastStore();
    const { user, setUser, isLoggedIn, logout } = useAuthStore();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 950);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 950);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    // NOTE: use this api for RBAC functionalities as per need
    // const getMeApiData = async () => {
    //   try {
    //     const myDetails = await axios.get(
    //       process.env.VITE_APPSEC_AUTH_BACKEND_URL + "/api/user/me",
    //       { withCredentials: true }
    //     );
    //     return myDetails.data;
    //   } catch (error) {
    //     console.error("Error fetching me api details from the backend:", error);
    //   }
    // };
    const handleUpdateCWDatabase = async (userData) => {
        try {
            if (!userData) {
                console.error("No user data available");
                return;
            }
            const { responseData, error } = await updateCWDatabase(userData);
            if (responseData) {
                console.log("CW Database updated successfully:", responseData.detail, responseData?.message);
            }
            else {
                console.error(error);
            }
        }
        catch (error) {
            console.error("Error updating the CW database:", error);
        }
    };
    const handleUpdateCWUserScopes = async (userEmail, scopes) => {
        try {
            if (!userEmail && !scopes) {
                console.error("No user data available");
                return;
            }
            const { responseData, error } = await setCwUserScopes({
                email: userEmail,
                scopes: scopes,
            });
            if (responseData) {
                // console.log(responseData.detail ?? responseData?.message);
            }
            else {
                console.error(error);
            }
        }
        catch (error) {
            console.error("Error updating the CW database:", error);
        }
    };
    const handleListUsers = async () => {
        try {
            const { responseData, error } = await listCWUsers();
            if (responseData) {
            }
            else {
                console.error(error);
            }
        }
        catch (error) {
            console.error("Error fetching the list of users:", error);
        }
    };
    const handleGetCWUser = async (userEmail) => {
        try {
            if (!userEmail) {
                console.warn("No user email available");
                return;
            }
            const { responseData, error } = await getCWUser(userEmail);
            if (responseData) {
                // console.log("User fetched successfully:", responseData._id);
                return responseData;
            }
            else {
                console.error(error);
            }
        }
        catch (error) {
            console.error("Error fetching the user from CW database:", error);
        }
    };
    useEffect(() => {
        const getSession = async () => {
            try {
                const { responseData, error } = await getUserSession();
                // console.log("session api response:", responseData);
                if (error) {
                    console.error("error in getting session", error);
                    showToast("error", "Error", "Error in getting session, check console for more details.");
                }
                else {
                    if (responseData) {
                        if (responseData?.user) {
                            setUser(responseData?.user);
                            // Note: Set the onboarding step as "done", if required
                            // setOnboardingStep("done");
                            // const myDetailsData = await getMeApiData();
                            // commented out as we are using its response anywhere
                            // await handleListUsers();
                            const cwUser = await handleGetCWUser(responseData.user.email);
                            if (!cwUser ||
                                !cwUser.email ||
                                cwUser.email !== responseData.user.email) {
                                await handleUpdateCWDatabase(responseData?.user);
                            }
                            else {
                                await handleUpdateCWUserScopes(responseData.user.email, responseData?.user?.provider?.scope);
                            }
                        }
                        else {
                            logout();
                        }
                    }
                }
            }
            catch (error) {
                console.error("error in getting session", error);
                showToast("error", "Error", "Error in getting session, check console for more details.");
                logout();
            }
            finally {
                setIsLoading(false); // Ensure state updates after session check
            }
        };
        getSession();
    }, []);
    if (isMobile)
        return _jsx(UnsupportedMobileScreen, {});
    if (isLoading)
        return (_jsx(AppLoading, {}));
    return (_jsx(AppErrorBoundary, { children: _jsx(Suspense, { fallback: _jsx(AppLoading, {}), children: _jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: isLoggedIn ? _jsx(Navigate, { to: "/chat", replace: true }) : _jsx(AuthPage, {}) }), _jsx(Route, { path: "/chat", element: isLoggedIn ? _jsx(AppLayout, {}) : _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/chat/:chatId", element: isLoggedIn ? _jsx(AppLayout, {}) : _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/chat", replace: true }) }), _jsx(Route, { path: "/settings", element: isLoggedIn ? _jsx(SettingsPage, {}) : _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/project-access", element: _jsx(ProjectAccess, {}) }), _jsx(Route, { path: "*", element: _jsx("div", { children: "Page not found" }) })] }) }) }));
};
export default App;
